"use client";
import { API_URL } from "@/lib/config";

import { useCallback, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  Bell, Star, CheckCircle2, BarChart2, ShoppingBag, Check,
  UserPlus, Heart, MessageCircle, X,
} from "lucide-react";
import type { NotificationResponse as NotificationItem } from "@/api-client/types.gen";

const API     = API_URL;
const WS_BASE = (API_URL)
  .replace(/^http/, "ws");

// ── Helpers ───────────────────────────────────────────────────────────────────

function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const s = Math.floor(diff / 1000);
  if (s < 60)  return "just now";
  const m = Math.floor(s / 60);
  if (m < 60)  return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24)  return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

const TYPE_META: Record<string, { Icon: React.ElementType; color: string; label: string }> = {
  review:        { Icon: Star,           color: "text-amber-400",   label: "New review"    },
  processing:    { Icon: CheckCircle2,   color: "text-emerald-400", label: "Upload ready"  },
  digest:        { Icon: BarChart2,      color: "text-blue-400",    label: "Weekly digest" },
  product_click: { Icon: ShoppingBag,   color: "text-purple-400",  label: "Product click" },
  new_follower:  { Icon: UserPlus,       color: "text-indigo-400",  label: "New follower"  },
  new_like:      { Icon: Heart,          color: "text-pink-400",    label: "New like"      },
  new_comment:   { Icon: MessageCircle, color: "text-sky-400",     label: "New comment"   },
};

// ── Toast component ───────────────────────────────────────────────────────────

interface ToastNotif { id: string; title: string; body?: string; type: string }

function NotifToast({ notif, onDismiss }: { notif: ToastNotif; onDismiss: () => void }) {
  const meta = TYPE_META[notif.type] ?? TYPE_META.review;
  const Icon = meta.Icon;

  useEffect(() => {
    const t = setTimeout(onDismiss, 5000);
    return () => clearTimeout(t);
  }, [onDismiss]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 24, scale: 0.96 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 8, scale: 0.96 }}
      transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
      className="flex items-start gap-3 w-80 bg-tone-900/95 backdrop-blur-xl border border-ink/12 rounded-2xl shadow-2xl px-4 py-3 pointer-events-auto"
    >
      <div className={`mt-0.5 shrink-0 ${meta.color}`}>
        <Icon className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-ink text-xs font-semibold leading-snug">{notif.title}</p>
        {notif.body && (
          <p className="text-tone-400 text-xs mt-0.5 leading-snug line-clamp-2">{notif.body}</p>
        )}
      </div>
      <button onClick={onDismiss} className="p-0.5 rounded-md text-tone-600 hover:text-ink transition-colors shrink-0">
        <X className="w-3 h-3" />
      </button>
    </motion.div>
  );
}

// ── Component ─────────────────────────────────────────────────────────────────

interface NotificationBellProps {
  onUnreadChange?: (count: number) => void;
}

export default function NotificationBell({ onUnreadChange }: NotificationBellProps = {}) {
  const [items, setItems]       = useState<NotificationItem[]>([]);
  const [open, setOpen]         = useState(false);
  const [loading, setLoading]   = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [toasts, setToasts]     = useState<ToastNotif[]>([]);
  const panelRef                = useRef<HTMLDivElement>(null);
  const wsRef                   = useRef<WebSocket | null>(null);
  const pollRef                 = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 639px)");
    setIsMobile(mq.matches);
    const h = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mq.addEventListener("change", h);
    return () => mq.removeEventListener("change", h);
  }, []);

  const unread = items.filter((n) => !n.read).length;

  useEffect(() => { onUnreadChange?.(unread); }, [unread, onUnreadChange]);

  const fetch_ = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/users/me/notifications`, { credentials: "include" });
      if (!res.ok) return;
      setItems(await res.json());
    } catch { /* silent */ }
  }, []);

  // Polling fallback (60 s)
  useEffect(() => {
    fetch_();
    pollRef.current = setInterval(fetch_, 60_000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [fetch_]);

  // WebSocket — connect once user is authenticated (ws-token endpoint confirms that)
  useEffect(() => {
    let ws: WebSocket | null = null;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    let destroyed = false;

    const connect = async () => {
      try {
        const res = await fetch(`${API}/api/auth/ws-token`, { credentials: "include" });
        if (!res.ok) return; // not logged in
        const { token } = await res.json();
        if (!token || destroyed) return;

        ws = new WebSocket(`${WS_BASE}/api/ws?token=${encodeURIComponent(token)}`);
        wsRef.current = ws;

        ws.onmessage = (ev) => {
          try {
            const msg = JSON.parse(ev.data);
            // Events that map to in-app notifications
            const notifTypes: Record<string, { title: string; body?: string }> = {
              new_follower:   { title: `${msg.follower_username ?? "Someone"} followed you` },
              new_like:       { title: "Someone liked your panorama", body: msg.panorama_name },
              new_comment:    { title: "New comment on your panorama", body: msg.body },
              "account.banned": { title: "Your account has been suspended", body: msg.reason },
            };
            const notif = notifTypes[msg.type];
            if (notif) {
              const toast: ToastNotif = { id: crypto.randomUUID(), type: msg.type, ...notif };
              setToasts((prev) => [...prev.slice(-3), toast]); // keep at most 4
              fetch_(); // refresh bell list
            }
          } catch { /* malformed message */ }
        };

        ws.onclose = () => {
          if (!destroyed) {
            reconnectTimer = setTimeout(connect, 5000); // reconnect after 5 s
          }
        };
      } catch { /* network error — retry handled by onclose */ }
    };

    connect();
    return () => {
      destroyed = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      ws?.close();
    };
  }, [fetch_]);

  const dismissToast = (id: string) =>
    setToasts((prev) => prev.filter((t) => t.id !== id));

  const handleOpen = () => {
    setOpen((v) => !v);
    if (!open) fetch_();
  };

  // Close on outside click
  useEffect(() => {
    if (!open || isMobile) return;
    const onDown = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open, isMobile]);

  const markAllRead = async () => {
    if (unread === 0) return;
    setLoading(true);
    try {
      await fetch(`${API}/api/users/me/notifications/read`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({}),
      });
      setItems((prev) => prev.map((n) => ({ ...n, read: true })));
    } catch { /* silent */ }
    finally { setLoading(false); }
  };

  const panelContent = (
    <>
      {isMobile && (
        <div className="flex justify-center pt-3 pb-1">
          <div className="w-10 h-1 rounded-full bg-ink/20" />
        </div>
      )}
      <div className="flex items-center justify-between px-4 py-3 border-b border-tone-800">
        <span className="text-ink font-semibold text-sm">Notifications</span>
        {unread > 0 && (
          <button onClick={markAllRead} disabled={loading}
            className="flex items-center gap-1 text-blue-400 hover:text-blue-300 text-xs transition-colors disabled:opacity-50">
            <Check size={11} /> Mark all read
          </button>
        )}
      </div>
      <div className="overflow-y-auto divide-y divide-tone-800/60"
        style={{ maxHeight: isMobile ? "calc(60vh - 56px)" : "24rem" }}>
        {items.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-10 gap-2 text-tone-600">
            <Bell size={24} />
            <p className="text-sm">You&apos;re all caught up</p>
          </div>
        ) : (
          items.slice(0, 20).map((n) => {
            const meta = TYPE_META[n.type] ?? TYPE_META.review;
            return (
              <div key={n.id}
                className={`flex gap-3 px-4 py-3 transition-colors ${n.read ? "opacity-60" : "bg-ink/[0.02]"}`}>
                <div className={`mt-0.5 shrink-0 ${meta.color}`}>
                  <meta.Icon size={15} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-ink text-sm font-medium leading-snug">{n.title}</p>
                  {n.body && (
                    <p className="text-tone-400 text-xs mt-0.5 leading-snug line-clamp-2">{n.body}</p>
                  )}
                  <p className="text-tone-600 text-xs mt-1">{relativeTime(n.created_at)}</p>
                </div>
                {!n.read && <div className="mt-1.5 w-1.5 h-1.5 rounded-full bg-blue-500 shrink-0 text-white" />}
              </div>
            );
          })
        )}
      </div>
    </>
  );

  return (
    <>
      {/* Toast portal — fixed bottom-right */}
      <div className="fixed bottom-6 right-6 z-[500] flex flex-col gap-2 items-end pointer-events-none">
        <AnimatePresence>
          {toasts.map((t) => (
            <NotifToast key={t.id} notif={t} onDismiss={() => dismissToast(t.id)} />
          ))}
        </AnimatePresence>
      </div>

      {/* Bell button */}
      <div className="relative" ref={panelRef}>
        <button onClick={handleOpen}
          className="relative p-1 rounded-full bg-tone-800/80 hover:bg-tone-700 border border-tone-700 text-tone-400 hover:text-ink transition-all"
          title="Notifications">
          <Bell className="w-4 h-4" />
          {unread > 0 && (
            <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-0.5 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center leading-none">
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </button>

        <AnimatePresence>
          {open && isMobile && (
            <motion.div key="notif-backdrop" className="fixed inset-0 z-[199] bg-black/50"
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }} onClick={() => setOpen(false)} />
          )}
          {open && (
            <motion.div key="notif-panel"
              initial={isMobile ? { y: "100%" } : { opacity: 0, y: -6, scale: 0.97 }}
              animate={isMobile ? { y: 0 }    : { opacity: 1, y: 0,   scale: 1    }}
              exit={isMobile   ? { y: "100%" } : { opacity: 0, y: -6, scale: 0.97 }}
              transition={isMobile
                ? { type: "spring", damping: 30, stiffness: 300 }
                : { duration: 0.15 }}
              className={isMobile
                ? "fixed bottom-0 inset-x-0 z-[200] bg-tone-900 border-t border-tone-800 rounded-t-2xl shadow-2xl shadow-black/60 overflow-hidden"
                : "absolute right-0 top-full mt-2 w-80 bg-tone-900 border border-tone-800 rounded-2xl shadow-2xl shadow-black/60 overflow-hidden z-[200]"}
              style={isMobile ? { paddingBottom: "var(--sai-bottom, 0px)" } : undefined}>
              {panelContent}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </>
  );
}
